import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const STEPS = [
  {
    number: '01',
    icon: 'ri-user-settings-line',
    title: 'Join & Configure\nYour Acquisition Profile',
    description: 'Select your service plan and define your investment parameters — which states, certificate types (tax lien vs. tax deed), target face values, and maximum risk tolerance. Our onboarding team configures your profile within 24 hours.',
    details: [
      'Choose from Starter (CA), Pro (all 3 states), or Elite (unlimited + custom)',
      'Define target certificate face value range (e.g., $5K–$150K)',
      'Select preferred property types and counties',
      'Set acquisition authorization threshold (auto-approve or manual review)',
      'Configure notification preferences for auction alerts',
    ],
    insight: 'Most members complete their profile setup in under 20 minutes.',
  },
  {
    number: '02',
    icon: 'ri-radar-line',
    title: 'We Monitor &\nSource Opportunities',
    description: 'Our specialists continuously track tax sale schedules, delinquency rolls, and foreclosure filings across every county in California, Nevada, and Arizona. When we find certificates matching your criteria, you\'re alerted immediately.',
    details: [
      'Daily monitoring of county tax collector schedules in all three states',
      'Real-time delinquency roll review for upcoming sale candidates',
      'AI-assisted filtering against your investment criteria',
      'Pro & Elite members receive alerts 30–90 days before public sale',
      'Each opportunity includes a full due-diligence summary before you approve',
    ],
    insight: 'Pro members average 12–15 qualified alerts per month across CA, NV, and AZ.',
  },
  {
    number: '03',
    icon: 'ri-auction-line',
    title: 'We Bid, Acquire\n& Handle All Paperwork',
    description: 'Once you authorize an acquisition, our specialists attend the tax sale on your behalf — physically or electronically depending on the county. We bid strategically within your approved parameters and handle all post-sale paperwork.',
    details: [
      'Physical or online attendance at county tax sales',
      'Strategic competitive bidding within your approved maximum',
      'Immediate post-sale notification with acquisition confirmation',
      'All legal filings, recording fees, and documentation handled',
      'Title review and lien priority analysis before every bid',
      'Full acquisition report delivered to your dashboard within 48 hours',
    ],
    insight: 'Our bid team achieves a 97% acquisition success rate on authorized opportunities.',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Track, Collect\n& Grow Your Portfolio',
    description: 'After acquisition, we track every certificate in your portfolio — redemption deadlines, interest accrual, and redemption notifications. You watch your returns grow through your real-time member dashboard.',
    details: [
      'Real-time dashboard showing all active certificates and statuses',
      'Automated redemption deadline tracking and alerts',
      'Interest accrual calculator updated daily',
      'Redemption payment processing and distribution to your account',
      'Annual yield reports and tax documentation',
      'Portfolio growth analytics with projected returns',
    ],
    insight: 'Our members\' certificates average a 14.2% annual return across all portfolio types.',
  },
];

const TIMELINE = [
  { phase: 'Day 1', event: 'Join & set acquisition profile' },
  { phase: 'Day 1–2', event: 'Our team configures your criteria' },
  { phase: 'Week 1–2', event: 'First qualified alerts arrive in your dashboard' },
  { phase: 'Week 2–4', event: 'Authorize first acquisition opportunities' },
  { phase: 'Sale Day', event: 'We bid & acquire certificates on your behalf' },
  { phase: 'Post-Sale', event: 'Certificate recorded, added to your portfolio' },
  { phase: 'Ongoing', event: 'Track returns, receive interest & redemption payments' },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="font-sans bg-[#090b10]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-1/3 w-[600px] h-[400px] rounded-full bg-gold/4 blur-[120px]" />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 border border-gold/25 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">Our Process</span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-6">
              How Our Certificate<br />
              <span className="text-gold">Acquisition Service Works</span>
            </h1>
            <p className="text-white/45 text-xl max-w-2xl mx-auto">
              A fully managed four-step process — from profile setup to portfolio growth. You authorize. We execute.
            </p>
          </div>
        </section>

        {/* Disclaimer Banner */}
        <div className="bg-amber-500/8 border-y border-amber-500/15">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-400 text-base" />
            </div>
            <p className="text-white/50 text-sm leading-relaxed">
              <span className="text-amber-400 font-semibold">Important:</span> All tax lien certificates and tax deeds are purchased directly through your county government office. Foreclosure Recovery provides research, data, and acquisition forms only — we are not a broker, dealer, or seller of tax certificates.
            </p>
          </div>
        </div>

        {/* Step tabs */}
        <div className="bg-surface-2 border-y border-white/5 sticky top-20 z-40">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex overflow-x-auto">
              {STEPS.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  className={`flex-1 min-w-[130px] flex flex-col items-center py-4 px-3 border-b-2 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeStep === idx ? 'border-gold text-white' : 'border-transparent text-white/30 hover:text-white/60'
                  }`}
                >
                  <span className={`text-sm font-bold mb-0.5 ${activeStep === idx ? 'text-gold' : ''}`}>{step.number}</span>
                  <span className="text-xs hidden sm:block">{step.title.split('\n')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step detail */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            {STEPS.map((step, idx) => (
              <div key={idx} className={activeStep === idx ? 'block' : 'hidden'}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                  <div>
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gold/10 border border-gold/20 flex-shrink-0">
                        <i className={`${step.icon} text-gold text-2xl`} />
                      </div>
                      <div>
                        <span className="text-gold text-xs font-bold uppercase tracking-widest">Step {step.number}</span>
                        <h2 className="font-playfair text-3xl font-bold text-white mt-0.5 whitespace-pre-line leading-tight">{step.title}</h2>
                      </div>
                    </div>
                    <p className="text-white/50 text-lg leading-relaxed mb-8">{step.description}</p>
                    <ul className="flex flex-col gap-4">
                      {step.details.map((d, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-gold/15 flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-gold text-xs" />
                          </div>
                          <span className="text-white/50 text-sm leading-relaxed">{d}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 p-5 bg-gold/5 border border-gold/15 rounded-xl">
                      <div className="flex items-start gap-3">
                        <i className="ri-lightbulb-line text-gold text-lg flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gold text-xs font-bold mb-1 uppercase tracking-wide">Key Insight</p>
                          <p className="text-white/50 text-sm leading-relaxed">{step.insight}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:sticky lg:top-36">
                    <div className="bg-surface-2 border border-white/8 rounded-2xl overflow-hidden h-72 mb-5">
                      <img
                        src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg${encodeURIComponent(
                          idx === 0 ? 'financial investment profile setup digital dashboard dark interface professional fintech onboarding screen data entry modern' :
                          idx === 1 ? 'data monitoring radar intelligence sourcing financial opportunities digital analytics dashboard dark screen professional' :
                          idx === 2 ? 'legal document signing financial acquisition professional courthouse legal filing certificate professional dark business' :
                          'portfolio tracking dashboard financial returns growth analytics charts dark professional fintech investment platform'
                        )}&width=800&height=600&seq=hiw_new_${idx + 1}&orientation=landscape`}
                        alt={step.title}
                        className="w-full h-full object-cover object-center opacity-70"
                      />
                    </div>
                    <div className="flex gap-3">
                      {idx > 0 && (
                        <button onClick={() => setActiveStep(idx - 1)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-surface-2 border border-white/8 text-white/50 rounded-xl text-sm hover:text-white hover:border-white/20 transition-colors cursor-pointer whitespace-nowrap">
                          <i className="ri-arrow-left-line" /> Previous
                        </button>
                      )}
                      {idx < STEPS.length - 1 ? (
                        <button onClick={() => setActiveStep(idx + 1)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-gold text-navy-dark rounded-xl text-sm font-bold hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap">
                          Next Step <i className="ri-arrow-right-line" />
                        </button>
                      ) : (
                        <Link to="/pricing" className="flex-1 flex items-center justify-center gap-2 py-3 bg-gold text-navy-dark rounded-xl text-sm font-bold hover:bg-gold-light transition-colors whitespace-nowrap">
                          View Service Plans <i className="ri-arrow-right-line" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="bg-surface-1 border-t border-white/5 py-20">
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-4xl font-bold text-white">Your First 30 Days</h2>
              <p className="text-white/40 mt-3">From joining to your first certificate in the portfolio.</p>
            </div>
            <div className="relative">
              <div className="absolute left-[88px] top-0 bottom-0 w-px bg-white/5 hidden md:block" />
              <div className="flex flex-col gap-0">
                {TIMELINE.map((item, i) => (
                  <div key={i} className="flex items-start gap-6 py-4">
                    <div className="w-[88px] flex-shrink-0 text-right pr-6 pt-0.5">
                      <span className="text-gold text-xs font-bold">{item.phase}</span>
                    </div>
                    <div className="relative flex-shrink-0 mt-1 hidden md:block">
                      <div className={`w-3 h-3 rounded-full border-2 ${i === 0 ? 'bg-gold border-gold' : 'bg-surface-1 border-white/20'}`} />
                    </div>
                    <p className="text-white/55 text-sm pt-0.5">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-5">Ready to Get Started?</h2>
            <p className="text-white/40 text-lg mb-8">Our specialists are acquiring certificates every week. Join and your profile could be active within 24 hours.</p>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-10 py-4 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap text-lg">
              View Service Plans <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
